import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Sheet, cn } from '@ai-tutor/ui';
import { SourcesPanel } from '../sources/SourcesPanel';
import { LessonViewer } from '../lesson/LessonViewer';
import { DoubtsExplorePanel } from '../chat/DoubtsExplorePanel';
import { LessonTopBar, type SourcesBadgeState } from './LessonTopBar';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { useHideOnScrollDown } from '@/hooks/ui/useHideOnScrollDown';
import { subscribeChatPrefill } from '@/lib/chat-prefill';
import { displayLessonTitle } from '@/lib/lesson-overview';
import type { Source } from '@/data/fixtures';
import type {
  LessonMetadata,
  SourceItem as ApiSourceItem,
} from '@/lib/api-client';
import type { StepView, SourcesView } from '@/types/pipeline';

export interface LessonCompactLayoutProps {
  lessonId?: string;
  lessonMetadata?: LessonMetadata;
  steps?: Record<string, StepView>;
  sources?: SourcesView;
  lessonDone?: boolean;
  lessonReady?: boolean;
  onRetry?: () => void;
  currentVersion: string;
  onVersionChange: (version: string) => void;
  selectedSource: Source | null;
  onSourceSelect: (source: Source | ApiSourceItem) => void;
  /** The lesson's source rows (API mode) - for the top bar's badge. */
  apiSources: ApiSourceItem[];
  scopeSource: ApiSourceItem | null;
  scopePendingCount: number;
  onOpenScope: () => void;
}

type LessonSheet = 'sources' | 'chat';

/**
 * Which sheet is open, held in the URL (`?sheet=sources|chat`) like the
 * lesson's tabs (`useLessonTabParam`): a reload keeps it, a link can open
 * one, and a static story frame can show one. Writes replace the history
 * entry, as tab changes do.
 */
function useSheetParam(): [
  LessonSheet | null,
  (next: LessonSheet | null) => void,
] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('sheet');
  const value = raw === 'sources' || raw === 'chat' ? raw : null;
  const setValue = useCallback(
    (next: LessonSheet | null) => {
      setParams(
        prev => {
          if (prev.get('sheet') === next) return prev;
          const p = new URLSearchParams(prev);
          if (next) p.set('sheet', next);
          else p.delete('sheet');
          return p;
        },
        { replace: true }
      );
    },
    [setParams]
  );
  return [value, setValue];
}

/**
 * The open sheet, plus the one thing besides a tap that opens one: "Explain"
 * (and any other chat prefill) opens the chat it is writing into.
 */
function useLessonSheet() {
  const [sheet, setSheet] = useSheetParam();
  const closeSheet = useCallback(() => setSheet(null), [setSheet]);
  useEffect(() => subscribeChatPrefill(() => setSheet('chat')), [setSheet]);
  return [sheet, setSheet, closeSheet] as const;
}

/**
 * Ask hides while reading downward; a new tab or sub-tab shows it again.
 *
 * Off on Practice altogether: there the tutor is offered where it is wanted
 * - "Ask the tutor why" on a quiz answer, "Ask about this card" on a
 * flashcard - and a floating button sat over the quiz's pinned action bar.
 */
function useAskHidden(areaRef: React.RefObject<HTMLDivElement>) {
  const [params] = useSearchParams();
  const scrolledAway = useHideOnScrollDown(
    areaRef,
    `${params.get('tab')}/${params.get('view')}`
  );
  return scrolledAway || params.get('tab') === 'practice';
}

/** Count and state for the top bar's sources button. */
function sourcesBadge(rows: ApiSourceItem[]): {
  count: number;
  state: SourcesBadgeState;
} {
  const active = rows.filter(r => r.is_active);
  const busy = active.some(
    r => r.status === 'pending' || r.status === 'processing'
  );
  const attention = active.some(
    r => r.status === 'failed' || r.status === 'awaiting_scope'
  );
  return {
    count: active.length,
    state: busy ? 'busy' : attention ? 'attention' : 'idle',
  };
}

/**
 * The lesson below `lg` (phones and tablets): the lesson owns the screen and
 * the two side panels become sheets you call up.
 *
 * - Sources (and Prompt, and versions) open full-screen from the top bar's
 *   sources button - a deliberate management task, not something to read
 *   beside the lesson.
 * - The chat opens from the Ask button as a tall sheet (a right-hand drawer
 *   on a tablet). It stays MOUNTED while closed: it keeps its conversation,
 *   and it must still be subscribed when "Explain" on selected text asks it
 *   to fill its input. The old phone layout mounted the chat only while its
 *   menu was open, so Explain went nowhere.
 *
 * Both sheets mount ONE copy of their panel. The desktop layout is not
 * mounted at all at this width (see `LessonLearningLayout`).
 */
export function LessonCompactLayout(props: LessonCompactLayoutProps) {
  const {
    lessonId,
    lessonMetadata,
    steps,
    sources,
    lessonDone,
    lessonReady,
    onRetry,
    currentVersion,
    onVersionChange,
    selectedSource,
    apiSources,
    scopeSource,
    scopePendingCount,
    onOpenScope,
  } = props;
  const [sheet, setSheet, closeSheet] = useLessonSheet();
  const isPhone = useMediaQuery('(max-width: 767px)');
  const areaRef = useRef<HTMLDivElement>(null);
  const askHidden = useAskHidden(areaRef);

  const badge = sourcesBadge(apiSources);
  const title = displayLessonTitle(lessonMetadata, steps?.overview?.state);

  // The chapter picker is a dialog of its own; nothing should sit over it.
  const openScope = () => {
    setSheet(null);
    onOpenScope();
  };

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      <LessonTopBar
        lessonId={lessonId}
        title={title}
        sourceCount={badge.count}
        sourcesState={badge.state}
        onOpenSources={() => setSheet('sources')}
        showShellAccessory={isPhone}
      />

      {/* Paper: one solid surface under the lesson. The page's grid lattice
          belongs behind the app's frame, not behind text - it ran through
          every line on a phone, where there is no panel card to hide it. */}
      <div
        ref={areaRef}
        className='relative min-h-0 flex-1 overflow-hidden bg-card'
      >
        <LessonViewer
          lessonId={lessonId}
          currentVersion={currentVersion}
          onVersionChange={onVersionChange}
          selectedSource={selectedSource}
          steps={steps}
          sources={sources}
          hasUserSources={lessonMetadata?.has_user_sources}
          lessonDone={lessonDone}
          lessonReady={lessonReady}
          onRetry={onRetry}
          scopeSource={scopeSource}
          scopePendingCount={scopePendingCount}
          onOpenScope={openScope}
          tabPlacement='bottom'
        />

        <AskButton
          hidden={askHidden}
          expanded={sheet === 'chat'}
          onClick={() => setSheet('chat')}
        />
      </div>

      <LessonSheets
        sheet={sheet}
        side={isPhone ? 'bottom' : 'right'}
        onClose={closeSheet}
        onOpenScope={openScope}
        panels={props}
      />
    </div>
  );
}

interface LessonSheetsProps {
  sheet: LessonSheet | null;
  /** Off the bottom edge on a phone, the right edge on a tablet. */
  side: 'bottom' | 'right';
  onClose: () => void;
  onOpenScope: () => void;
  panels: Pick<
    LessonCompactLayoutProps,
    | 'lessonId'
    | 'lessonMetadata'
    | 'steps'
    | 'sources'
    | 'lessonDone'
    | 'currentVersion'
    | 'onVersionChange'
    | 'onSourceSelect'
    | 'scopeSource'
  >;
}

/** The chat sheet (kept mounted) and the sources sheet (mounted on open). */
function LessonSheets({
  sheet,
  side,
  onClose,
  onOpenScope,
  panels,
}: LessonSheetsProps) {
  const { lessonId, steps, lessonDone, currentVersion } = panels;
  return (
    <>
      <Sheet
        isOpen={sheet === 'chat'}
        onClose={onClose}
        side={side}
        size='tall'
        ariaLabel='Lesson chat'
        keepMounted
        showHeader={false}
      >
        <DoubtsExplorePanel
          lessonId={lessonId}
          // Strip the display-only "v" prefix - the backend stores and
          // filters by the bare numeric ("1.0"), as the desktop panel does.
          currentVersion={currentVersion?.replace(/^v/, '')}
          steps={steps}
          lessonDone={lessonDone}
          awaitingScope={!!panels.scopeSource}
          onOpenScope={onOpenScope}
          onClose={onClose}
        />
      </Sheet>

      <Sheet
        isOpen={sheet === 'sources'}
        onClose={onClose}
        side={side}
        size='full'
        ariaLabel='Sources and prompt'
        showHeader={false}
      >
        <SourcesPanel
          lessonId={lessonId}
          currentVersion={currentVersion}
          onVersionChange={panels.onVersionChange}
          // Picking a source shows it: `onSourceSelect` closes the sheet onto
          // Appendix › Source Viewer in the same URL write (`sheet` dropped).
          onSourceSelect={panels.onSourceSelect}
          lessonMetadata={panels.lessonMetadata}
          steps={steps}
          sources={panels.sources}
          lessonDone={lessonDone}
          onClose={onClose}
        />
      </Sheet>
    </>
  );
}

/**
 * Floats above the bottom tab bar (3.5rem + the home-indicator inset), and
 * slides away while the reader scrolls down so it never sits on the line
 * being read; scrolling up brings it back.
 */
function AskButton({
  hidden,
  expanded,
  onClick,
}: {
  hidden: boolean;
  expanded: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label='Ask your tutor'
      aria-haspopup='dialog'
      aria-expanded={expanded}
      tabIndex={hidden ? -1 : undefined}
      className={cn(
        'absolute bottom-[calc(4.25rem+env(safe-area-inset-bottom))] right-4 z-20 flex h-11 items-center gap-1.5 rounded-full bg-primary pl-3.5 pr-4 text-sm font-semibold text-primary-foreground shadow-elevation-2 transition-[transform,opacity] duration-200 active:scale-95 motion-reduce:transition-none',
        hidden && 'pointer-events-none translate-y-6 opacity-0'
      )}
    >
      <Sparkles className='h-4 w-4' aria-hidden='true' />
      Ask
    </button>
  );
}
